// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

/** Target sample rate RNNoise is trained for. */
export const RNNOISE_RATE = 48000;

/** Output audio formats we can encode. */
export type OutputFormat = 'wav' | 'mp3';

/** A decoded, mono, 48 kHz recording ready for the denoiser. */
export interface PreparedAudio {
  /** Mono PCM, normalised to [-1, 1], 48 kHz. */
  samples: Float32Array;
  sampleRate: number;
  /** Original file/recording name (for the download filename). */
  sourceName: string;
  /** Original duration in seconds. */
  duration: number;
}

/** Result of a denoise pass. */
export interface DenoiseResult {
  /** The fully wet (denoised) mono signal at 48 kHz. */
  wet: Float32Array;
  /** The original mono signal at 48 kHz (for A/B + dry/wet mixing). */
  dry: Float32Array;
  sampleRate: number;
  /** Estimated broadband noise floor of the input, dBFS. */
  noiseFloorInDb: number;
  /** Estimated broadband noise floor of the output, dBFS. */
  noiseFloorOutDb: number;
  /** Fraction of frames RNNoise flagged as speech (VAD >= 0.5). */
  speechRatio: number;
}

/** Messages the denoise worker posts back to the main thread. */
export type DenoiseWorkerMessage =
  | { type: 'ready' }
  | { type: 'progress'; processed: number; total: number }
  | {
      type: 'done';
      wet: ArrayBuffer;
      noiseFloorInDb: number;
      noiseFloorOutDb: number;
      speechRatio: number;
    }
  | { type: 'error'; message: string };

/** Messages the encode worker posts back. */
export type EncodeWorkerMessage =
  | { type: 'progress'; ratio: number }
  | { type: 'done'; buffer: ArrayBuffer; mime: string }
  | { type: 'error'; message: string };
